import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanRelatedClassApplicationList,
  fetchYowanRelatedClassDetail,
} from '../app/yowanContentClient';
import {
  getStakeholderClassHref,
  StakeholderGuardNotice,
  StakeholderSearchToolbar,
  StakeholderShell,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
import {
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const applicationSearchText = (row = {}) => [
  row.applicantName,
  row.applicantNameKana,
  row.applicantEmail,
  row.scheduleLabel,
  row.statusLabel,
  row.message,
].filter(Boolean).join(' ').toLowerCase();

const StakeholderClassApplicationsPage = ({ guardOptions }) => {
  const { classId } = useParams();
  const content = yowanPageContent['/stakeholder/classes/:classId/applications'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [classRecord, setClassRecord] = useState(null);
  const [records, setRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    Promise.all([
      fetchYowanRelatedClassDetail(classId).catch(() => null),
      fetchYowanRelatedClassApplicationList({ classId }),
    ])
      .then(([classPayload, applicationPayload]) => {
        if (!isMounted) {
          return;
        }

        setClassRecord(applicationPayload.classRecord || classPayload);
        setRecords(applicationPayload.results || []);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setClassRecord(null);
        setRecords([]);
        setLoadError(content?.loadErrorMessage || '申込一覧を読み込めませんでした。');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [classId, content?.loadErrorMessage, guard.status]);

  const visibleRecords = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return records;
    }

    return records.filter((row) => applicationSearchText(row).includes(normalizedQuery));
  }, [records, searchQuery]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  return (
    <StakeholderShell
      activeNavHref="/stakeholder/classes"
      routeKey="/stakeholder/classes/:classId/applications"
      currentUser={guard.currentUser}
    >
      <AdminPageHeader
        title={classRecord ? `${classRecord.title} 申込一覧` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="detail" to={getStakeholderClassHref(classId, preservedDomainSearch)}>
            詳細
          </Link>,
        ]}
      />

      <StakeholderSearchToolbar
        searchPlaceholder={content.searchPlaceholder}
        searchValue={searchQuery}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        metaText={`${visibleRecords.length}件表示`}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        {isLoading ? <LoadingSpinner label="申込一覧を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError && visibleRecords.length === 0 ? (
          <p className="yowan-admin-form__message">表示できる申込はありません。</p>
        ) : null}
        {!isLoading && !loadError && visibleRecords.length > 0 ? (
          <table className="yowan-admin-resource-table yowan-stakeholder-table yowan-stakeholder-table--class-applications">
            <thead>
              <tr>
                <th>申込者</th>
                <th>希望日・枠</th>
                <th>ステータス</th>
                <th>メッセージ</th>
                <th>申込日時</th>
              </tr>
            </thead>
            <tbody>
              {visibleRecords.map((row) => (
                <tr key={row.id}>
                  <td>
                    <p className="yowan-admin-resource-table__title">{row.applicantName || '-'}</p>
                    {row.applicantNameKana ? <p className="yowan-admin-resource-table__subline">{row.applicantNameKana}</p> : null}
                    {row.applicantEmail ? <p className="yowan-admin-resource-table__subline">{row.applicantEmail}</p> : null}
                  </td>
                  <td>
                    <p className="yowan-admin-resource-table__subline">{row.scheduleLabel || '-'}</p>
                    {row.desiredDate ? <p className="yowan-admin-resource-table__subline">希望日: {row.desiredDate}</p> : null}
                  </td>
                  <td>{row.statusLabel || '-'}</td>
                  <td>
                    <p className="yowan-stakeholder-message">{row.message || '-'}</p>
                  </td>
                  <td>{row.createdAt || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </StakeholderShell>
  );
};

export default StakeholderClassApplicationsPage;
